/**
 * Document kinds that live in the `page`/`block` model as a SINGLE serialized
 * content block (blob-per-document), discriminated by `block.type`.
 *
 * Spreadsheets (`/sheets`) and slide decks (`/slides`) are persisted local-first
 * WITHOUT any new table or schema change: each document is a `page` (title,
 * trash, version history, multi-org sharing — all reused) plus ONE `block` whose
 * `id === page.id` and whose `type` is one of these kinds. The whole workbook /
 * deck is JSON-serialized into `block.content`; `block.text` carries a plaintext
 * projection for search and version diffs.
 *
 * Because the block's id is deterministically the page id, autosave via
 * `syncPageBlocksMutation` is a stable single-row upsert (diff-by-id never
 * churns), and the server's generic `block` write-path + version snapshotting
 * apply unchanged (no Rust edit).
 */
export const WORKBOOK_BLOCK_TYPE = "workbook";
export const DECK_BLOCK_TYPE = "deck";
/**
 * PDF annotation overlay: the PDF file itself never leaves the device
 * (local-first / privacy); what we persist is the annotation set, keyed by the
 * PDF's content fingerprint, as a blob document page. Re-opening the same file
 * re-attaches its annotations.
 */
export const PDF_ANNOTATIONS_BLOCK_TYPE = "pdf-annotations";

/** All block types that mark a `page` as a non-note office document. */
export const DOCUMENT_BLOCK_TYPES = [
  WORKBOOK_BLOCK_TYPE,
  DECK_BLOCK_TYPE,
  PDF_ANNOTATIONS_BLOCK_TYPE,
] as const;

export type DocumentKind = (typeof DOCUMENT_BLOCK_TYPES)[number];

export function isDocumentBlockType(type: string | null | undefined): type is DocumentKind {
  return (DOCUMENT_BLOCK_TYPES as readonly string[]).includes(type ?? "");
}

/**
 * SIDECAR block types: rows that live on a page but are NOT editor content.
 * They ride along with the page (sync, sharing, trash cascade) yet must stay
 * invisible to the content pipeline:
 *  - `syncPageBlocksMutation` (autosave diff-by-id) must never delete them,
 *  - `blocksToDoc` / blob `pickContentBlock` must skip them,
 *  - restoring a version must neither wipe nor resurrect them.
 *
 * Today: comment/suggestion threads (`comment-thread`, one row per thread,
 * `block.id === thread.id`, JSON thread in `block.content`).
 */
export const COMMENT_THREAD_BLOCK_TYPE = "comment-thread";

export const SIDECAR_BLOCK_TYPES = [COMMENT_THREAD_BLOCK_TYPE] as const;

export function isSidecarBlockType(type: string | null | undefined): boolean {
  return (SIDECAR_BLOCK_TYPES as readonly string[]).includes(type ?? "");
}
